/**
 * Loan product-type helpers — the single source of truth for "does this loan
 * carry construction/rehab economics?".
 *
 * DSCR (and the pure bridge products) have no construction budget, holdback,
 * draws, or after-repair value — those fields must be hidden on their
 * drilldown. Historically every surface re-derived this differently
 * (`=== "DSCR"`, substring `"dscr"`/`"rental"`, hardcoded 2-product sets),
 * which is why construction fields leaked onto DSCR loans. Route every
 * construction/budget gate through here.
 *
 * The positive set mirrors the backend `arv` / `totalHoldback` `conditional`
 * metadata (`app/models/pipeline.py`) plus the rehab products that legitimately
 * carry a budget. See DSCR_CONSTRUCTION_FIELD_CLEANUP.md.
 */

// Products that carry a construction / rehab budget, holdback, draws, and ARV.
export const CONSTRUCTION_PRODUCTS = new Set([
  "Fix and Flip",
  "Ground Up Construction",
  "HVRTL - Fix and Flip",
  "Multifamily Value Add",
  "Other", // legacy — backend conditional includes it
  "Vacant Land", // legacy — not in product_options, inert but kept for parity
]);

/** True when the loan's product carries construction/budget/holdback/ARV. */
export const isConstructionProduct = (loanProductType) =>
  CONSTRUCTION_PRODUCTS.has(loanProductType);

// Interest reserves (`interestReserves` + `financedReserve`, and the loan-level
// roll-ups) show on every product except DSCR — the owner's DSCR display rules
// (dscr-drilldown-display-rules.md, 2026-08-25) only take them off DSCR. The
// backend puts no product gate on the field. Requested by Shyrose (2026-10-08).

/** True when the loan's product shows interest reserves (everything but DSCR). */
export const isInterestReserveProduct = (loanProductType) =>
  loanProductType !== "DSCR";

/** True for a DSCR loan. */
export const isDscrProduct = (loanProductType) => loanProductType === "DSCR";
